import React from 'react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Column } from '../../components/DataTable/DataTable';
import { TruncatedText } from '../../components/DataTable/TruncatedText';
import { Conversation } from './useData';
import { FieldConfig } from '../../components/generic/fieldRenderers';
import { FormLayout } from '../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, formatDateTime, APP_CONFIG } from '../../config/appConfig';
import type { ThemeColors } from '../../contexts/ThemeContext';
import { StandardTableActions, Badge } from '../../components/ui';

// Module Metadata
export const moduleConfig = {
  // Module Information
  module: {
    title: 'Conversations',
    description: 'Manage conversations for tickets and tasks',
    route: {
      list: '/conversations',
      new: '/conversations/new',
      detail: (id: number) => `/conversations/${id}`,
      edit: (id: number) => `/conversations/${id}/edit`,
    },
    // API Endpoints
    api: {
      list: '/conversations',
      detail: (id: number) => `/conversations/${id}`,
    },
  },

  // Pagination Settings
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },

  // Search Settings
  search: {
    placeholder: 'Search conversations by message, sender, or ticket/task...',
  },

  // Filter Groups Configuration
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'conversationType', 'senderType', 'ticketId', 'taskId', 'isRead', 'createdAt'
      ],
    },
  ],

  // Filter Configuration
  filters: {
    fields: [
      {
        key: 'conversationType',
        label: 'Type',
        placeholder: 'All Types',
        type: 'select' as const,
        getValue: (item: Conversation) => item.conversationType,
        options: ['TICKET', 'TASK'],
      },
      {
        key: 'senderType',
        label: 'Sender Type',
        placeholder: 'All Sender Types',
        type: 'select' as const,
        getValue: (item: Conversation) => item.senderType,
        options: ['STAFF', 'CLIENT'],
      },
      {
        key: 'ticketId',
        label: 'Ticket',
        placeholder: 'All Tickets',
        type: 'select' as const,
        getValue: (item: Conversation) => item.ticketId?.toString() || '',
        options: [],
      },
      {
        key: 'taskId',
        label: 'Task',
        placeholder: 'All Tasks',
        type: 'select' as const,
        getValue: (item: Conversation) => item.taskId?.toString() || '',
        options: [],
      },
      {
        key: 'isRead',
        label: 'Read Status',
        placeholder: 'All Status',
        type: 'boolean' as const,
        getValue: (item: Conversation) => item.isRead ? 'Read' : 'Unread',
        options: ['Read', 'Unread'],
      },
      {
        key: 'createdAt',
        label: 'Created At',
        placeholder: 'All Created At',
        type: 'date' as const,
        getValue: (item: Conversation) =>
          item.createdAt
            ? formatDateGlobal(item.createdAt)
            : undefined,
        options: [],
      },
    ],
    // Filter logic functions
    applyFilter: (
      items: Conversation[],
      filters: {
        conversationType: string[];
        senderType: string[];
        ticketId: string[];
        taskId: string[];
        isRead: string[];
        createdAt: string[];
      },
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>
    ) => {
      const ranges = dateRanges || {};
      return items.filter((item) => {
        // Conversation type filter
        if (filters.conversationType && filters.conversationType.length > 0) {
          if (!filters.conversationType.includes(item.conversationType)) return false;
        }

        // Sender type filter
        if (filters.senderType && filters.senderType.length > 0) {
          if (!filters.senderType.includes(item.senderType)) return false;
        }

        // Ticket filter
        if (filters.ticketId && filters.ticketId.length > 0) {
          if (!item.ticketId || !filters.ticketId.includes(item.ticketId.toString())) return false;
        }

        // Task filter
        if (filters.taskId && filters.taskId.length > 0) {
          if (!item.taskId || !filters.taskId.includes(item.taskId.toString())) return false;
        }

        // Read status filter
        if (filters.isRead && filters.isRead.length > 0) {
          const readStatus = item.isRead ? 'Read' : 'Unread';
          if (!filters.isRead.includes(readStatus)) return false;
        }

        // Date range filter
        if (ranges.createdAt) {
          const itemDate = item.createdAt ? new Date(item.createdAt) : null;
          if (ranges.createdAt.start && itemDate && itemDate < ranges.createdAt.start) return false;
          if (ranges.createdAt.end && itemDate && itemDate > ranges.createdAt.end) return false;
        }

        return true;
      });
    },
  },

  // Form Fields Configuration
  fields: [
    {
      name: 'conversationType',
      label: 'Conversation Type',
      type: 'select',
      required: true,
      options: [
        { value: 'TICKET', label: 'Ticket' },
        { value: 'TASK', label: 'Task' },
      ],
      validation: {
        required: true,
      },
    },
    {
      name: 'ticketId',
      label: 'Ticket',
      type: 'select',
      required: false,
      options: [],
      apiEndpoint: '/support-tickets',
      labelField: 'ticketTitle',
      validation: {
        required: false,
      },
    },
    {
      name: 'taskId',
      label: 'Task',
      type: 'select',
      required: false,
      options: [],
      apiEndpoint: '/crm-tasks',
      labelField: 'taskDescription',
      validation: {
        required: false,
      },
    },
    {
      name: 'message',
      label: 'Message',
      type: 'textarea',
      required: true,
      rows: 8,
      validation: {
        required: true,
        minLength: 1,
      },
    },
    {
      name: 'senderType',
      label: 'Sender Type',
      type: 'select',
      required: true,
      options: [
        { value: 'STAFF', label: 'Staff' },
        { value: 'CLIENT', label: 'Client' },
      ],
      validation: {
        required: true,
      },
    },
    {
      name: 'staffUserId',
      label: 'Staff User',
      type: 'select',
      required: false,
      options: [],
      apiEndpoint: '/admin/users',
      labelField: 'userDisplayName',
      validation: {
        required: false,
      },
    },
    {
      name: 'clientId',
      label: 'Client',
      type: 'select',
      required: false,
      options: [],
      apiEndpoint: '/crm-clients',
      labelField: 'clientName',
      validation: {
        required: false,
      },
    },
    {
      name: 'replyToId',
      label: 'Reply To',
      type: 'select',
      required: false,
      options: [],
      validation: {
        required: false,
      },
    },
  ] as FieldConfig[],

  // Form Layout Configuration (crm-tasks style: gridCols + fieldSpans)
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Conversation Details',
        icon: '💬',
        gridCols: 'md:grid-cols-12',
        fields: ['conversationType', 'ticketId', 'taskId', 'senderType', 'staffUserId', 'clientId'],
        fieldSpans: {
          conversationType: 'md:col-span-6',
          ticketId: 'md:col-span-6',
          taskId: 'md:col-span-6',
          senderType: 'md:col-span-6',
          staffUserId: 'md:col-span-6',
          clientId: 'md:col-span-6',
        },
        collapsible: true,
      },
      {
        id: 'message',
        title: 'Message',
        icon: '📝',
        gridCols: 'md:grid-cols-12',
        fields: ['message', 'replyToId'],
        fieldSpans: {
          message: 'md:col-span-12',
          replyToId: 'md:col-span-6',
        },
        collapsible: true,
      },
    ],
  } as FormLayout,

  // Detail Configuration
  detail: {
    subtitle: 'Conversation Details',
    notFoundTitle: 'Conversation not found',
    notFoundMessage: 'The conversation you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Conversations',
    defaultTitle: 'Conversation',
    getTitle: (item: Conversation) => `Conversation #${item.id}`,
  },

  // Table Configuration
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: Conversation) => {
      window.location.href = `/conversations/${item.id}`;
    },
    keyExtractor: (item: Conversation) => item.id,
  },

  // Toolbar Actions
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'New Conversation',
        showTitle: false,
        variant: 'default' as const,
        href: '/conversations/new',
      },
    ],
  },

  // Empty States
  emptyStates: {
    noData: 'No conversations found',
    noSearchResults: 'No conversations match your search',
    noFilterResults: 'No conversations match your filters',
  },

  // Delete Configuration
  delete: {
    title: 'Delete Conversation',
    message: 'Are you sure you want to delete this conversation? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete conversation',
    handler: async (id: number) => {
      const { deleteConversation } = await import('./useData');
      await deleteConversation(id);
    },
  },

  // Utility Functions
  utils: {
    formatDate: formatDateGlobal,
    formatDateTime: formatDateTime,
  },

  // Get Columns Function (will be exported separately)
  getColumns: (formatDate: (dateString?: string) => string, _categories?: any, colors?: ThemeColors): Column<Conversation>[] => [
    {
      key: 'id',
      header: 'ID',
      render: (item: Conversation) => (
        <Link
          to={`/conversations/${item.id}`}
          className={colors ? `${colors.primaryFg} hover:underline` : 'text-indigo-600 hover:text-indigo-800 hover:underline'}
        >
          {item.id}
        </Link>
      ),
      sortable: true,
    },
    {
      key: 'conversationType',
      header: 'Type',
      render: (item: Conversation) => (
        <Badge variant={item.conversationType === 'TICKET' ? 'warning' : 'info'}>
          {item.conversationType}
        </Badge>
      ),
      sortable: true,
    },
    {
      key: 'message',
      header: 'Message',
      render: (item: Conversation) => (
        <TruncatedText text={item.message || ''} maxLength={100} />
      ),
      sortable: false,
    },
    {
      key: 'sender',
      header: 'Sender',
      render: (item: Conversation) => {
        if (item.senderType === 'STAFF' && item.staffUser) {
          return item.staffUser.userDisplayName || 
            `${item.staffUser.userFirstName || ''} ${item.staffUser.userLastName || ''}`.trim() ||
            `User #${item.staffUser.id}`;
        }
        if (item.senderType === 'CLIENT' && item.client) {
          return item.client.clientName || `Client #${item.client.clientId}`;
        }
        return item.senderType;
      },
      sortable: false,
    },
    {
      key: 'ticketId',
      header: 'Ticket',
      render: (item: Conversation) => {
        if (item.ticketId) {
          return (
            <Link
              to={`/support-tickets/${item.ticketId}`}
              className={colors ? `${colors.primaryFg} hover:underline` : 'text-indigo-600 hover:text-indigo-800 hover:underline'}
            >
              #{item.ticketId}
            </Link>
          );
        }
        return '-';
      },
      sortable: false,
    },
    {
      key: 'taskId',
      header: 'Task',
      render: (item: Conversation) => {
        if (item.taskId) {
          return (
            <Link
              to={`/crm_tasks/${item.taskId}`}
              className={colors ? `${colors.primaryFg} hover:underline` : 'text-indigo-600 hover:text-indigo-800 hover:underline'}
            >
              #{item.taskId}
            </Link>
          );
        }
        return '-';
      },
      sortable: false,
    },
    {
      key: 'isRead',
      header: 'Status',
      render: (item: Conversation) => (
        <Badge variant={item.isRead ? 'secondary' : 'info'}>
          {item.isRead ? 'Read' : 'Unread'}
        </Badge>
      ),
      sortable: true,
    },
    {
      key: 'createdAt',
      header: 'Created At',
      render: (item: Conversation) => formatDateTime(item.createdAt),
      sortable: true,
    },
  ],

  // Get Toolbar Icon (will be exported separately)
  getToolbarIcon: (iconName: string): React.ReactNode => {
    const icons: Record<string, React.ReactNode> = {
      add: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      download: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      ),
    };
    return icons[iconName] || null;
  },

  // Get Table Actions
  getTableActions: (
    item: Conversation,
    onDelete: (id: number) => void,
    _colors?: ThemeColors,
    _listContext?: unknown,
    options?: { canDelete?: boolean; canEdit?: boolean }
  ): React.ReactNode => {
    const canDelete = options?.canDelete !== false;
    return (
      <StandardTableActions
        viewTo={`/conversations/${item.id}`}
        onDelete={() => onDelete(item.id)}
        canEdit={false}
        canDelete={canDelete}
      />
    );
  },

  // Get Initial Form Data
  getInitialFormData: (): Partial<Conversation> => ({
    conversationType: 'TICKET',
    senderType: 'STAFF',
    message: '',
  }),

  // Map Item to Form Data
  mapItemToFormData: (item: Conversation): Partial<Conversation> => ({
    conversationType: item.conversationType,
    ticketId: item.ticketId,
    taskId: item.taskId,
    message: item.message,
    senderType: item.senderType,
    staffUserId: item.staffUserId,
    clientId: item.clientId,
    replyToId: item.replyToId,
  }),

  // Form Configuration
  form: {
    new: {
      title: 'New Conversation',
      subtitle: 'Create a new conversation message',
      submitText: 'Create Conversation',
    },
    edit: {
      title: 'Edit Conversation',
      subtitle: 'Update conversation details',
      submitText: 'Update Conversation',
    },
    cancelText: 'Cancel',
    loadingText: 'Saving...',
    errorMessage: 'Failed to save conversation',
    create: async (data: Partial<Conversation>) => {
      const { createConversation } = await import('./useData');
      return createConversation(data as any);
    },
    update: async (id: number, data: Partial<Conversation>) => {
      const { updateConversation } = await import('./useData');
      return updateConversation(id, data as any);
    },
  },
};

// Helper function to get detail sections
export const getDetailSections = (
  item: Conversation,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📋',
      fields: [
        {
          label: 'ID',
          value: item.id.toString(),
        },
        {
          label: 'Type',
          value: item.conversationType,
        },
        {
          label: 'Sender Type',
          value: item.senderType,
        },
        {
          label: 'Created At',
          value: formatDateTime(item.createdAt),
        },
      ],
    },
    {
      title: 'Message',
      icon: '💬',
      fields: [
        {
          label: '',
          value: (
            <div
              className="prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.message || '') }}
            />
          ),
        },
      ],
    },
  ];
};

// Helper function to get status badges
export const getStatusBadges = (item: Conversation, _colors?: ThemeColors) => {
  return (
    <>
      <Badge variant={item.conversationType === 'TICKET' ? 'warning' : 'info'}>
        {item.conversationType}
      </Badge>
      <Badge variant={item.isRead ? 'secondary' : 'info'}>
        {item.isRead ? 'Read' : 'Unread'}
      </Badge>
    </>
  );
};

// Export functions separately for use in List component
export const getColumns = (formatDate: (dateString?: string) => string, categories?: any, colors?: ThemeColors): Column<Conversation>[] =>
  moduleConfig.getColumns(formatDate, categories, colors);

export const getToolbarIcon = (iconName: string): React.ReactNode =>
  moduleConfig.getToolbarIcon(iconName);

export const getTableActions = (
  item: Conversation,
  onDelete: (id: number) => void,
  colors?: ThemeColors,
  listContext?: unknown,
  options?: { canDelete?: boolean }
): React.ReactNode =>
  moduleConfig.getTableActions(item, onDelete, colors, listContext, options);

export type ModuleConfig = typeof moduleConfig;
